import { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import { useMobileTheme } from "../mobile/hooks/useMobileTheme.js";
import { isNativeMobileApp } from "../utils/googleAuth.js";

// Tracks window.location.pathname without needing Router context — this
// component is mounted above the app's <BrowserRouter> (see App.jsx), so it
// can't use useLocation(). pushState/replaceState are wrapped (in addition
// to the native popstate event) purely to notify this listener; behavior of
// navigation itself is unchanged for every other consumer.
const usePathname = () => {
    const [pathname, setPathname] = useState(() => window.location.pathname);

    useEffect(() => {
        const update = () => setPathname(window.location.pathname);
        window.addEventListener("popstate", update);
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;
        window.history.pushState = function patchedPushState(...args) {
            originalPushState.apply(this, args);
            update();
        };
        window.history.replaceState = function patchedReplaceState(...args) {
            originalReplaceState.apply(this, args);
            update();
        };
        return () => {
            window.removeEventListener("popstate", update);
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
        };
    }, []);

    return pathname;
};

// Keeps the native status bar's icon/text color in sync with the app's own
// light/dark theme instead of leaving it at the OS default, which otherwise
// reads as unfinished (e.g. dark status bar icons over a dark header).
// While inside the /mobile/* native shell, the mobile app's own Appearance
// setting (useMobileTheme) drives the status bar instead of the website's
// ThemeContext, so the two independent theme systems never fight.
const NativeStatusBarSync = () => {
    const { activeMode } = useTheme() || {};
    const { resolvedMode: mobileResolvedMode } = useMobileTheme();
    const pathname = usePathname();
    const isMobileShell = pathname.startsWith("/mobile");
    const effectiveMode = isMobileShell ? mobileResolvedMode : activeMode;

    useEffect(() => {
        if (!isNativeMobileApp()) return;

        import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
            StatusBar.setStyle({ style: effectiveMode === "dark" ? Style.Dark : Style.Light }).catch(() => {});
        });
    }, [effectiveMode]);

    return null;
};

export default NativeStatusBarSync;
